/**
 * Authenticated session storage + lifecycle.
 *
 * - Refresh token + cached user persist in localStorage so a tab refresh
 *   doesn't kick the user back to /login.
 * - Access token lives in memory only (security — limits XSS exposure).
 * - On boot, the FE has a refresh token but no access token; the http client
 *   transparently calls /auth/refresh to mint a new access token before the
 *   first protected request.
 *
 * See docs/BACKEND_CONTRACT_ADDENDUM_AUTH.md §1.1.
 */
import type { ActiveSession, CurrentUser, PersistedSession } from '@/types/auth';
import { appEvents } from './events';

const STORAGE_KEY = 'zafroi.session.v1';

let active: ActiveSession | null = null;
const listeners = new Set<(session: ActiveSession | null) => void>();

function notify(): void {
  listeners.forEach((fn) => fn(active));
}

function readPersisted(): PersistedSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedSession;
  } catch {
    return null;
  }
}

function writePersisted(p: PersistedSession | null): void {
  try {
    if (p) localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* swallow — private browsing, full disk, etc. */
  }
}

export const session = {
  /** True if a refresh token is on disk (we may or may not have an active access token yet). */
  hasPersistedSession(): boolean {
    return readPersisted() !== null;
  },

  /** Returns the cached user without hitting the API — useful for instant topbar render at boot. */
  cachedUser(): CurrentUser | null {
    return readPersisted()?.user ?? null;
  },

  /** Returns the in-memory active session if logged in. */
  current(): ActiveSession | null {
    return active;
  },

  /** Get the refresh token from disk (used by http client for /auth/refresh). */
  getRefreshToken(): string | null {
    return readPersisted()?.refreshToken ?? null;
  },

  /** Set a complete active session — called after /login or /accept-invite or /refresh. */
  set(next: ActiveSession): void {
    active = next;
    writePersisted({
      refreshToken: next.refreshToken,
      user: next.user,
    });
    notify();
  },

  /**
   * Update access token after /auth/refresh. Two scenarios:
   *
   *  1. Already-active session in memory (token expired mid-session) —
   *     keep the existing user + just rotate the tokens.
   *  2. Fresh page load — `active` is null but localStorage has the
   *     persisted refresh token + cached user from a prior session.
   *     The first /auth/refresh on boot lands here; we must bootstrap
   *     the in-memory active session from the persisted user, else
   *     the retried request goes out with no Authorization header
   *     and the BE 401s the whole chain back to /login.
   */
  updateAccessToken(accessToken: string, refreshToken: string, expiresAt: string): void {
    if (active) {
      active = { ...active, accessToken, refreshToken, expiresAt };
    } else {
      const persisted = readPersisted();
      if (!persisted?.user) return;
      active = { accessToken, refreshToken, expiresAt, user: persisted.user };
    }
    writePersisted({ refreshToken, user: active.user });
    notify();
  },

  /** Update the cached user without changing tokens (e.g. after /auth/me). */
  updateUser(user: CurrentUser): void {
    if (active) {
      active = { ...active, user };
    }
    const persisted = readPersisted();
    if (persisted) {
      writePersisted({ ...persisted, user });
    }
    notify();
  },

  /** Wipe everything. Triggers `auth-required` so listeners (router) can react. */
  clear(): void {
    active = null;
    writePersisted(null);
    notify();
    appEvents.emit('auth-required', { reason: 'logout' });
  },

  /** Subscribe — returns unsubscribe. */
  subscribe(fn: (session: ActiveSession | null) => void): () => void {
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  },
};
